import React from 'react'
import styled from 'styled-components'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import GraphicFeatureSection from '../components/GraphicFeatureSection'
import HowItWorksSection from '../components/HowItWorksSection'
import SuperheroSliderSection from '../components/SuperheroSliderSection'
import { HeroStrip } from '../components/InfiniteHeroSection'
import TakeActionSection from '../components/TakeActionSection'
import JourneyStepsSection from '../components/JourneyStepsSection'
import ResearchRepositorySection from '../components/ResearchRepositorySection'
import Vision2030Section from '../components/Vision2030Section'
import ScienceCommunitySection from '../components/ScienceCommunitySection'
import ReadyToJoinSection from '../components/ReadyToJoinSection'
import FutureImpactSection from '../components/FutureImpactSection'
import PartnerWithUsSection from '../components/PartnerWithUsSection'
import {
  AnimatedSection,
  fadeInUp,
  fadeIn,
  smoothEase,
  StaggerContainer,
  StaggerItem,
} from '../components/animations/AnimatedSection'
import { Variants } from 'framer-motion'

// Hero-specific entrance variants (play on mount, not on scroll)
const heroTitleVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: smoothEase, delay: 0.2 },
  },
}

const heroSubtitleVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: smoothEase, delay: 0.5 },
  },
}

const heroButtonVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: smoothEase, delay: 0.8 },
  },
}

const logoFloatVariants: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 0.1,
    scale: 1,
    transition: { duration: 1.5, ease: smoothEase, delay: 0.3 },
  },
  drifting: {
    y: [0, -15, 0],
    rotate: [0, 2, 0],
    transition: {
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
}

const HomePage = () => {
  const navigate = useNavigate()
  const { scrollYProgress } = useScroll()
  const yParallaxLogo = useTransform(scrollYProgress, [0, 0.5], [0, -100])

  return (
    <PageContainer>
      <HeroSection>
        {/* The two big background logos */}
        <MotionLogoLeft
          src="/images/logos/Logo-Symbol.svg"
          alt=""
          width="438"
          height="438"
          initial="hidden"
          animate={['visible', 'drifting']}
          variants={logoFloatVariants}
          style={{ y: yParallaxLogo }}
        />
        <MotionLogoRight
          src="/images/logos/Logo-Symbol.svg"
          alt=""
          width="438"
          height="438"
          initial="hidden"
          animate={['visible', 'drifting']}
          variants={logoFloatVariants}
          style={{ y: yParallaxLogo }}
        />

        <HeroContent>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroTitleVariants}
          >
            <Title>
              Translating Scientific
              <br />
              Knowledge into
              <br />
              <Highlight>Collective Action</Highlight>
            </Title>
          </motion.div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroSubtitleVariants}
          >
            <Subtitle>
              A global framework uniting researchers, educators, governments,
              and the next generation to solve the world&apos;s most pressing
              challenges through the UN Sustainable Development Goals.
            </Subtitle>
          </motion.div>
        </HeroContent>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={heroButtonVariants}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <StickyBar>
            <ButtonGroup>
              <ActiveButton onClick={() => navigate('/quiz')}>
                Find Your Superhero
              </ActiveButton>
              <InactiveButton onClick={() => navigate('/coming-soon')}>
                Join the Community
              </InactiveButton>
            </ButtonGroup>
          </StickyBar>
        </motion.div>
      </HeroSection>

      <AboutSection>
        {/* Floating Side Images (Hidden on mobile) */}
        <FloatingImageLeft>
          <ImageLeft
            src="/images/homepage-new/left-image.webp"
            alt="Students planting trees"
            width="250"
            height="300"
          />
        </FloatingImageLeft>
        <FloatingImageRight>
          <ImageRight
            src="/images/homepage-new/researcher.webp"
            alt="Researcher"
            width="260"
            height="320"
          />
        </FloatingImageRight>

        <StaggerContainer threshold={0.1}>
          <SectionWrapper>
            <StaggerItem>
              <SectionLabel>ABOUT US</SectionLabel>
            </StaggerItem>
            <StaggerItem variants={fadeInUp}>
              <SectionTitle>
                Where Research Meets
                <br />
                <Highlight>Real-World Impact</Highlight>
              </SectionTitle>
            </StaggerItem>
            <StaggerItem variants={fadeInUp}>
              <SectionSubtitle>
                We connect students, researchers, experts, and changemakers to
                transform research and ideas into real-world solutions aligned
                with the 17 Sustainable Development Goals (SDGs).
              </SectionSubtitle>
            </StaggerItem>

            <StaggerItem variants={fadeIn}>
              <ImageCenter
                src="/images/homepage-new/youth-climate-volunteer.webp"
                alt="Youth climate volunteer"
                width="450"
                height="280"
              />
            </StaggerItem>
          </SectionWrapper>
        </StaggerContainer>
      </AboutSection>

      <HeroStrip />

      <WhyMattersSection>
        <SectionWrapper>
          <AnimatedSection variants={fadeInUp}>
            <SectionLabel>WHY THIS MATTERS</SectionLabel>
          </AnimatedSection>
          <AnimatedSection variants={fadeInUp} delay={0.1}>
            <SectionTitle>
              From Knowledge to
              <br />
              <Highlight>Collective Action</Highlight>
            </SectionTitle>
          </AnimatedSection>
          <AnimatedSection variants={fadeInUp} delay={0.2}>
            <SectionSubtitle>
              The world doesn&apos;t lack knowledge about global risks but it
              lacks the infrastructure to convert that knowledge into
              legitimate, coordinated, and sustained action. Community of
              Guardians bridges this gap.
            </SectionSubtitle>
          </AnimatedSection>
        </SectionWrapper>
      </WhyMattersSection>

      <GraphicFeatureSection />

      <HowItWorksSection />

      <SuperheroSliderSection />

      <TakeActionSection />

      <JourneyStepsSection />

      <ResearchRepositorySection />

      <Vision2030Section />

      <ScienceCommunitySection id="science-community" />

      <ReadyToJoinSection />

      <FutureImpactSection />

      <PartnerWithUsSection />
    </PageContainer>
  )
}

const PageContainer = styled.div`
  width: 100%;
  position: relative;
  overflow-x: hidden;
`

const HeroSection = styled.section`
  position: relative;
  width: 100%;
  min-height: calc(100vh - 80px); /* Adjust if header is taller */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #dbef9f 0%, #f9f4fe 50%, #d9e7d8 100%);
  padding: 120px 24px 100px;
  overflow: hidden; /* Hide the overflow from large absolute images */
`

const MotionLogoLeft = styled(motion.img)`
  position: absolute;
  width: 438px;
  height: auto;
  left: -120px;
  top: 10%;
  pointer-events: none;

  @media (max-width: 768px) {
    display: none;
  }
`

const MotionLogoRight = styled(motion.img)`
  position: absolute;
  width: 438px;
  height: auto;
  right: -120px;
  top: 30%;
  transform: scaleX(-1);
  pointer-events: none;

  @media (max-width: 768px) {
    display: none;
  }
`

const HeroContent = styled.div`
  text-align: center;
  z-index: 10;
  max-width: 900px;
`

const Title = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 56px;
  line-height: 64px;
  text-align: center;
  color: #2a2a2a;
  max-width: 789px;
  margin: 0 auto 24px auto;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }
  @media (max-width: 480px) {
    font-size: 32px;
    line-height: 38px;
  }
`

const Highlight = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`

const Subtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
  color: #5e5e5e;
  max-width: 789px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`

const StickyBar = styled.div`
  margin-top: 80px;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    margin-top: 48px;
    padding: 0 24px;
    box-sizing: border-box;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: auto;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }
`

const BaseButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 14px 24px;
  gap: 10px;
  border-radius: 100px;
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-sizing: border-box;
  letter-spacing: 0px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 16px;
    font-size: 14px;
  }
`

const ActiveButton = styled(BaseButton)`
  background: #0e6409;
  border: 1px solid #2a2a2a;
  color: #ffffff;

  letter-spacing: 0px;

  &:hover {
    opacity: 0.9;
  }
`

const InactiveButton = styled(BaseButton)`
  background: transparent;
  color: #2a2a2a;
  border: 1px solid #2a2a2a;
  letter-spacing: 0px;

  &:hover {
    background: rgba(0, 0, 0, 0.05); /* very soft darken on hover */
  }
`

const AboutSection = styled.section`
  position: relative;
  width: 100%;
  min-height: auto;
  background-color: #faf9f6;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 24px;
  isolation: isolate;
  transform: translateZ(0);
`

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 32px;
  width: 100%;
  max-width: 732px;
  z-index: 10;
`

const SectionLabel = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 20px;
  text-align: center;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  color: #607e03;
  margin: 0;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 56px;
  line-height: 64px;
  text-align: center;
  letter-spacing: -0.04em;
  color: #2a2a2a;
  margin: 0;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 40px;
    line-height: 48px;
  }
`

const SectionSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
  color: #5e5e5e;
  margin: 0;
  max-width: 584px;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`

const WhyMattersSection = styled.section`
  width: 100%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 24px;

  @media (max-width: 768px) {
    padding: 48px 24px;
  }
`

const FloatingImageLeft = styled.div`
  position: absolute;
  left: 6%;
  top: 45%;
  transform: translateY(-50%) translateZ(0);
  z-index: 5;

  @media (max-width: 1150px) {
    display: none;
  }
`

const FloatingImageRight = styled.div`
  position: absolute;
  right: 6%;
  top: 40%;
  transform: translateY(-50%) translateZ(0);
  z-index: 5;

  @media (max-width: 1150px) {
    display: none;
  }
`

const ImageLeft = styled.img`
  width: 250px;
  height: 300px;
  object-fit: cover;
  border-radius: 24px;
  border: 2px solid #2a2a2a;
`

const ImageRight = styled.img`
  width: 260px;
  height: 320px;
  object-fit: cover;
  border-radius: 24px;
  border: 2px solid #2a2a2a;
`

const ImageCenter = styled.img`
  width: 450px;
  height: 280px;
  object-fit: cover;
  border-radius: 24px;
  border: 2px solid #2a2a2a;
  margin-top: 40px;
  margin-bottom: 20px;
  z-index: 5;

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 24px;
    height: auto;
  }
`

export default HomePage
